import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../services/firebase';
import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import FormRenderer from '../components/form-connect/FormRenderer';
import { Loader, CheckCircle, ArrowRight } from 'lucide-react';

const FormView = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  // Fetch form data
  useEffect(() => {
    const fetchForm = async () => {
      try {
        setLoading(true);
        
        const formRef = doc(db, 'forms', formId);
        const formSnap = await getDoc(formRef);
        
        if (formSnap.exists()) {
          setForm({
            id: formSnap.id,
            ...formSnap.data()
          });
        } else {
          setError('Form not found');
        }
      } catch (error) {
        console.error('Error fetching form:', error);
        setError('Error loading form. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    if (formId) {
      fetchForm();
    }
  }, [formId]);

  // Handle form submission
  const handleSubmit = async (formData, formattedData) => {
    try {
      // Increment submission count
      const formRef = doc(db, 'forms', formId);
      await updateDoc(formRef, {
        submissions: increment(1)
      });
      
      // In a real implementation, you might want to store the submission data
      // This is commented out as per the requirements to not store submissions yet
      /*
      await addDoc(collection(db, 'forms', formId, 'submissions'), {
        data: formData,
        submittedAt: serverTimestamp()
      });
      */
      
      setSubmitted(true);
      return true;
    } catch (error) {
      console.error('Error recording submission:', error);
      return false;
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center items-center min-h-[60vh]">
        <div className="text-center">
          <Loader className="animate-spin h-8 w-8 text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-gray-900">{error}</h3>
          <div className="mt-6">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render success message
  if (submitted) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-md">
        <div className="bg-gradient-to-br from-white to-indigo-50 rounded-lg shadow-xl p-8 text-center border border-indigo-100 transform transition-all duration-500 animate-fadeIn">
          <div className="mb-6 relative">
            <div className="absolute inset-0 bg-indigo-100 rounded-full opacity-30 animate-ping"></div>
            <CheckCircle className="mx-auto h-16 w-16 text-green-500 relative z-10" />
          </div>
          <h3 className="mt-2 text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
          <h4 className="text-lg font-medium text-indigo-700 mb-4">Form Submitted Successfully</h4>
          <p className="mt-1 text-gray-600 mb-6">
            Your message has been sent to WhatsApp. We appreciate your submission!
          </p>
          {form.settings?.redirectUrl ? (
            <div className="mt-6">
              <a
                href={form.settings.redirectUrl}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-lg text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-indigo-500 transform transition-all duration-300 hover:scale-105"
              >
                Continue
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          ) : (
            <div className="mt-6">
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-lg text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-indigo-500 transform transition-all duration-300 hover:scale-105"
              >
                Return Home
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <FormRenderer form={form} onSubmit={handleSubmit} />
      </div>
    </div>
  );
};

export default FormView;
